import QueryString from 'query-string';
import snakecaseKeys from 'snakecase-keys';

import { buildQueryParams as buildCustomFieldQueryParams } from 'bundles/apex/custom_fields/filters/helpers';
import { filterByTab } from 'bundles/comps/index/custom_fields/helpers';
import { get } from 'helpers/api';
import { stringify } from 'helpers/query_string_helpers';
import debounceActionCreators from 'helpers/redux/debounce_action_creators';
import latestLoad from 'helpers/redux/latest_load';
import RansackQuery from 'helpers/ransack_query';
import { isEmpty } from 'lodash';
import { industrialKeys, keys, leaseKeys, retrieveFilterOptions, saleKeys } from 'helpers/comps/api_helpers';

const debouncedRetrieveLeases = debounceActionCreators({ retrieveLeases }, 1000);
const debouncedRetrieveSales = debounceActionCreators({ retrieveSales }, 1000);

export const CHANGE_RECORDS = 'CHANGE_RECORDS';
export function changeRecords(sales, leases, meta) {
  return {
    leases,
    sales,
    meta,
    type: CHANGE_RECORDS
  };
}

export const RETRIEVE_RECORDS_STARTED = 'RETRIEVE_RECORDS_STARTED';
export function retrieveRecordsStarted() {
  return { type: RETRIEVE_RECORDS_STARTED };
}

// Each load replaces both lists, so a reply from a replaced load, or for the tab the user has left, is dropped: it
// can't empty the tab on screen or show the other tab's count.
const startLoad = latestLoad(state => state.ui.currentTab);

export function retrieveLeases() {
  return (dispatch, getState) => {
    // A sales search still waiting out its debounce would replace these leases when it fires.
    debouncedRetrieveSales.retrieveSales.cancel();
    const load = startLoad('lease', getState);
    if (load.showing()) dispatch(retrieveRecordsStarted());
    const { coordinates, paths, query, scope, ui } = getState();
    const filterOptions = retrieveFilterOptions(query, scope, ui.currentTab);
    const ransackQuery = new RansackQuery(
      filterOptions,
      { ...keys, ...leaseKeys, ...industrialKeys }
    ).toRansack();
    let path = paths.leases;
    const coordinatesQuery = isEmpty(coordinates.userBounds) ? coordinates.mapBounds : coordinates.userBounds;
    if (!isEmpty(coordinatesQuery)) {
      path = `${path}?${stringify(snakecaseKeys({ coordinates: coordinatesQuery }), { nested: true })}`;
    }
    return get(path, { params: { ...ransackQuery, ...getCustomFieldQueryParams(getState) } })
      .then((response) => {
        if (!load.current()) return;

        dispatch(changeRecords([], response.leases, response.meta));
      });
  };
}

export function retrieveSales() {
  return (dispatch, getState) => {
    debouncedRetrieveLeases.retrieveLeases.cancel();
    const load = startLoad('sale', getState);
    if (load.showing()) dispatch(retrieveRecordsStarted());
    const { coordinates, paths, query, scope, ui } = getState();
    const filterOptions = retrieveFilterOptions(query, scope, ui.currentTab);
    const ransackQuery = new RansackQuery(filterOptions, { ...keys, ...saleKeys, ...industrialKeys }).toRansack();
    let path = paths.sales;
    const coordinatesQuery = isEmpty(coordinates.userBounds) ? coordinates.mapBounds : coordinates.userBounds;
    if (!isEmpty(coordinatesQuery)) {
      path = `${path}?${stringify(snakecaseKeys({ coordinates: coordinatesQuery }), { nested: true })}`;
    }
    return get(path, { params: { ...ransackQuery, ...getCustomFieldQueryParams(getState) } })
      .then((response) => {
        if (!load.current()) return;

        dispatch(changeRecords(response.sales, [], response.meta));
      });
  };
}

export function delayRetrieveRecords() {
  return (dispatch, getState) => {
    const { ui } = getState();
    if (ui.currentTab === 'lease') {
      debouncedRetrieveLeases.retrieveLeases(dispatch);
    } else {
      debouncedRetrieveSales.retrieveSales(dispatch);
    }
  };
}

export function downloadLeasesAsCSV() {
  return (_dispatch, getState) => {
    const { paths, query, scope, selectList, ui } = getState();
    const filterOptions = retrieveFilterOptions(query, scope, ui.currentTab);
    const ransackQuery = new RansackQuery(filterOptions, { ...keys, ...leaseKeys }).toRansack();
    const selected = selectList.selectedLeaseIds;
    const queryString = QueryString.stringify({
      ...ransackQuery,
      ...getCustomFieldQueryParams(getState),
      selected,
    }, { arrayFormat: 'bracket' });

    window.location.assign(`${paths.leasesToCsv}?${queryString}`);
  };
}

export function downloadSalesAsCSV() {
  return (_dispatch, getState) => {
    const { paths, query, scope, selectList, ui } = getState();
    const filterOptions = retrieveFilterOptions(query, scope, ui.currentTab);
    const ransackQuery = new RansackQuery(filterOptions, { ...keys, ...saleKeys }).toRansack();
    const selected = selectList.selectedSaleIds;
    const queryString = QueryString.stringify({
      ...ransackQuery,
      ...getCustomFieldQueryParams(getState),
      selected,
    }, { arrayFormat: 'bracket' });

    window.location.assign(`${paths.salesToCsv}?${queryString}`);
  };
}

function getCustomFieldQueryParams(getState) {
  const state = getState();
  return buildCustomFieldQueryParams(
    state.customFieldSchemas,
    state.query.customFieldFilters,
    filterByTab(state.ui.currentTab)
  );
}
